import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

/**
 * App-level replacement for native alert()/confirm() dialogs.
 *
 * - `toast(message, variant)` — non-blocking notification, auto-dismisses.
 * - `confirm(opts)` — promise-based confirmation dialog.
 *
 * Mounted once at the app root (see App.tsx); consume via `useFeedback()`.
 */

type ToastVariant = 'error' | 'success' | 'info'

interface ToastItem {
  id: number
  message: string
  variant: ToastVariant
}

export interface ConfirmOptions {
  title?: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  /** Renders the confirm button in the destructive (red) style. */
  destructive?: boolean
}

interface FeedbackContextValue {
  toast: (message: string, variant?: ToastVariant) => void
  confirm: (opts: ConfirmOptions) => Promise<boolean>
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null)

// eslint-disable-next-line react-refresh/only-export-components -- hook is colocated with its provider by convention
export function useFeedback(): FeedbackContextValue {
  const ctx = useContext(FeedbackContext)
  if (!ctx) {
    throw new Error('useFeedback must be used within <FeedbackProvider>')
  }
  return ctx
}

const TOAST_DURATION_MS = 5000

const toastVariantStyles: Record<ToastVariant, string> = {
  error: 'border-destructive/50 text-destructive',
  success: 'border-primary/50 text-primary',
  info: 'border-border text-foreground',
}

interface ConfirmState {
  opts: ConfirmOptions
  resolve: (confirmed: boolean) => void
}

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null)
  const nextId = useRef(0)

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback(
    (message: string, variant: ToastVariant = 'info') => {
      const id = nextId.current++
      setToasts((prev) => [...prev, { id, message, variant }])
      window.setTimeout(() => dismissToast(id), TOAST_DURATION_MS)
    },
    [dismissToast]
  )

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({ opts, resolve })
    })
  }, [])

  const closeConfirm = useCallback(
    (confirmed: boolean) => {
      confirmState?.resolve(confirmed)
      setConfirmState(null)
    },
    [confirmState]
  )

  // Document-level Escape cancels the confirm dialog.
  useEffect(() => {
    if (!confirmState) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeConfirm(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [confirmState, closeConfirm])

  const value = useMemo(() => ({ toast, confirm }), [toast, confirm])

  return (
    <FeedbackContext.Provider value={value}>
      {children}

      {/* Toast list */}
      <div aria-live="polite" className="fixed right-4 bottom-4 z-[100] flex w-full max-w-sm flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              'bg-card flex items-start justify-between gap-3 rounded-xl border px-4 py-3 text-sm shadow-lg',
              toastVariantStyles[t.variant]
            )}
          >
            <span>{t.message}</span>
            <button
              type="button"
              onClick={() => dismissToast(t.id)}
              className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Confirm dialog */}
      {confirmState && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop dismiss; Escape handled at document level
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4"
          onClick={() => closeConfirm(false)}
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- stopPropagation guard so backdrop dismiss doesn't fire from clicks inside the dialog; Escape handled at document level */}
          <div
            role="alertdialog"
            aria-modal="true"
            aria-label={confirmState.opts.title ?? 'Confirm'}
            className="bg-card text-card-foreground w-full max-w-sm space-y-4 rounded-xl border p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {confirmState.opts.title && (
              <h2 className="text-lg leading-none font-semibold">{confirmState.opts.title}</h2>
            )}
            <p className="text-muted-foreground text-sm">{confirmState.opts.message}</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => closeConfirm(false)}
                className="border-input bg-background hover:bg-accent inline-flex h-9 items-center justify-center rounded-md border px-4 text-sm font-medium transition-all"
              >
                {confirmState.opts.cancelLabel ?? 'Cancel'}
              </button>
              <button
                type="button"
                // eslint-disable-next-line jsx-a11y/no-autofocus -- focus the primary action on dialog open
                autoFocus
                onClick={() => closeConfirm(true)}
                className={cn(
                  'inline-flex h-9 items-center justify-center rounded-md px-4 text-sm font-medium transition-all',
                  confirmState.opts.destructive
                    ? 'bg-destructive hover:bg-destructive/90 text-white'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                )}
              >
                {confirmState.opts.confirmLabel ?? 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </FeedbackContext.Provider>
  )
}
